import React from "react";
import {Box, Button, Container, Typography} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import InstagramIcon from "@mui/icons-material/Instagram";
import ExploreIcon from "@mui/icons-material/ExploreOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import {Link} from "react-router-dom";
import PageHeader from "../components/modern/PageHeader";
import SectionTitle from "../components/modern/SectionTitle";
import Reveal from "../components/modern/Reveal";
import EventList from "../components/modern/EventList";
import {today, useEvents} from "../data/sheet";
import {colors, displayFont} from "../styling/GlobalTheme";

const IMG = process.env.PUBLIC_URL + "/images/";
const MOUCHTEC_URL = "https://www.mouchtec.ch";
const MOUCHTEC_INSTA = "https://www.instagram.com/mouchtec/";

const pillars = [
    {icon: <ExploreIcon fontSize="large"/>, title: "Découvrir", text: "Le Mouch'Tec est ouvert à toutes les personnes qui souhaitent découvrir le télémark, le talon libre et ses sensations."},
    {icon: <TrendingUpIcon fontSize="large"/>, title: "Progresser", text: "Des activités régulières toute la saison pour améliorer sa technique, du premier virage jusqu'aux premières courses."},
    {icon: <GroupsIcon fontSize="large"/>, title: "Partager", text: "Plus de 20 jeunes entre 10 et 20 ans skient ensemble et font vivre l'esprit du club."},
];

const gallery = ["mouchtec/mouch_15.jpg", "2024mouchtec/mouch5.jpg", "2024mouchtec/mouch1.jpg", "mouchtec/mouch_9.jpg", "2024mouchtec/mouch7.jpg", "mouchtec/mouch_3.jpg", "club/enfants.jpg", "2024mouchtec/mouch9.jpg", "mouchtec/mouch_14.jpg"];

function Mouchtec() {
    const {data} = useEvents();
    const upcoming = data.filter(e => e.end >= today()).slice(0, 3);
    return (
        <>
            <PageHeader title="Mouch'Tec" subtitle="Le groupe d'entraînement du Mouch'Paba : découvrir le télémark et progresser ensemble."
                        image={IMG + "2024mouchtec/mouch3.jpg"}/>

            {/* Intro */}
            <Box component="section" sx={{py: {xs: 7, md: 10}}}>
                <Container maxWidth="lg" sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, gap: {xs: 5, md: 8}, alignItems: "center"}}>
                    <Reveal>
                        <Typography sx={{color: colors.glacierDark, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", fontSize: ".8rem"}}>La relève</Typography>
                        <Typography variant="h2" sx={{fontSize: {xs: "2.6rem", md: "3.6rem"}, mb: 2}}>Le télémark dès 10 ans</Typography>
                        <Typography sx={{color: "text.secondary", fontSize: "1.05rem", mb: 3}}>
                            Le Mouch'Tec est le groupe d'entraînement du Mouch'Paba. Il est ouvert à toutes les personnes qui souhaitent
                            découvrir le télémark ou améliorer leur technique. Actuellement, plus de 20 jeunes entre 10 et 20 ans en font partie,
                            et des activités régulières ont lieu toute la saison.
                        </Typography>
                        <Box sx={{display: "flex", gap: 3, mb: 4}}>
                            {[["20+", "jeunes"], ["10–20", "ans"], ["Toute", "la saison"]].map(([v, l]) => (
                                <Box key={l}>
                                    <Typography sx={{fontFamily: displayFont, fontSize: {xs: "2.4rem", md: "3rem"}, lineHeight: 1, color: colors.glacierDark}}>{v}</Typography>
                                    <Typography sx={{fontSize: ".85rem", color: "text.secondary"}}>{l}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap"}}>
                            <Button variant="contained" color="secondary" href={MOUCHTEC_URL} target="_blank" rel="noopener" endIcon={<ArrowOutwardIcon/>}>mouchtec.ch</Button>
                            <Button variant="outlined" color="inherit" href={MOUCHTEC_INSTA} target="_blank" rel="noopener" startIcon={<InstagramIcon/>}>Instagram</Button>
                        </Box>
                    </Reveal>
                    <Reveal delay={150}>
                        <Box sx={{position: "relative"}}>
                            <Box sx={{borderRadius: 5, overflow: "hidden", aspectRatio: "4 / 3", boxShadow: "0 30px 60px rgba(11,18,32,.2)"}}>
                                <img src={IMG + "mouchtec/mouch_5.jpg"} alt="Le groupe Mouch'Tec" style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                            </Box>
                            <Box sx={{display: {xs: "none", sm: "block"}, position: "absolute", width: "38%", aspectRatio: "1", bottom: -30, left: -30, borderRadius: 4, overflow: "hidden", border: "6px solid #fff", boxShadow: "0 20px 40px rgba(11,18,32,.2)"}}>
                                <img src={IMG + "mouchtec/mouch_9.jpg"} alt="" style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                            </Box>
                        </Box>
                    </Reveal>
                </Container>
            </Box>

            {/* Piliers */}
            <Box component="section" sx={{py: {xs: 7, md: 10}, bgcolor: colors.night, color: "#fff"}}>
                <Container maxWidth="lg">
                    <SectionTitle light kicker="Au Mouch'Tec" title="Découvrir, progresser, partager"/>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"}, gap: 2.5}}>
                        {pillars.map((p, i) => (
                            <Reveal key={p.title} delay={i * 120}>
                                <Box sx={{border: "1px solid #1f2b44", borderRadius: 4, p: 3.5, height: "100%", transition: ".3s", "&:hover": {borderColor: colors.glacier, transform: "translateY(-4px)"}}}>
                                    <Box sx={{color: colors.glacier, mb: 1.5}}>{p.icon}</Box>
                                    <Typography sx={{fontFamily: displayFont, fontSize: "2rem", letterSpacing: 1}}>{p.title}</Typography>
                                    <Typography sx={{color: "#aab4c5", mt: 1}}>{p.text}</Typography>
                                </Box>
                            </Reveal>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Galerie */}
            <Box component="section" sx={{py: {xs: 7, md: 10}}}>
                <Container maxWidth="lg">
                    <SectionTitle kicker="En images" title="Sur les pistes"/>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(4, 1fr)"}, gridAutoRows: {xs: 150, md: 200}, gap: 1.5}}>
                        {gallery.map((g, i) => (
                            <Reveal key={g} delay={(i % 4) * 80}
                                    style={{gridColumn: i === 0 ? "span 2" : undefined, gridRow: i === 0 ? "span 2" : undefined, borderRadius: 16, overflow: "hidden"}}>
                                <Box sx={{width: "100%", height: "100%", "&:hover img": {transform: "scale(1.06)"}}}>
                                    <img src={IMG + g} alt="Mouch'Tec" loading="lazy" style={{width: "100%", height: "100%", objectFit: "cover", transition: "transform .6s"}}/>
                                </Box>
                            </Reveal>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Prochaines activités */}
            <Box component="section" sx={{py: {xs: 7, md: 10}, bgcolor: "#fff"}}>
                <Container maxWidth="lg">
                    <SectionTitle kicker="Agenda" title="Prochaines activités"
                                  action={<Button component={Link} to="/activites" variant="outlined" color="inherit">Tout le programme</Button>}/>
                    <EventList events={upcoming} emptyText="Le programme de la saison sera publié prochainement."/>
                </Container>
            </Box>

            {/* Appel à l'action */}
            <Box component="section" sx={{position: "relative", py: {xs: 9, md: 12}, color: "#fff", textAlign: "center", overflow: "hidden", bgcolor: colors.night}}>
                <Box sx={{position: "absolute", inset: 0, backgroundImage: `url("${IMG}2024mouchtec/mouch6.jpg")`, backgroundSize: "cover", backgroundPosition: "center", opacity: .35}}/>
                <Container maxWidth="md" sx={{position: "relative"}}>
                    <Reveal>
                        <Typography variant="h2" sx={{fontSize: {xs: "2.8rem", md: "4rem"}}}>Envie d'essayer le talon libre ?</Typography>
                        <Typography sx={{opacity: .9, my: 2}}>Inscriptions et informations sur le site du Mouch'Tec.</Typography>
                        <Button size="large" variant="contained" color="secondary" href={MOUCHTEC_URL} target="_blank" rel="noopener" endIcon={<ArrowOutwardIcon/>}>Rejoindre le Mouch'Tec</Button>
                    </Reveal>
                </Container>
            </Box>
        </>
    );
}

export default Mouchtec;
